import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import { OfferWithImage } from '@/model/offer-with-image';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-offer-media-display',
  imports: [MatIconModule],
  templateUrl: './offer-media-display.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class OfferMediaDisplayComponent implements OnInit, OnChanges {
  @Input() offer!: OfferWithImage;
  @Input() showDownloadPdf = false;
  private _cachedVideoUrl: SafeResourceUrl | null = null;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit() {
    if (this.shouldShowVideo) {
      this._cachedVideoUrl = this.generateSafeVideoUrl();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['offer'] && !changes['offer'].firstChange) {
      if (this.shouldShowVideo) {
        this._cachedVideoUrl = this.generateSafeVideoUrl();
      }
    }
  }

  get shouldShowImage(): boolean {
    return (
      this.offer.offerType?.acceptedMimeType === 'image/*' &&
      !!this.offer.imageUrl
    );
  }

  get shouldShowVideo(): boolean {
    return (
      this.offer.offerType?.acceptedMimeType === 'video/*' &&
      !!this.offer.videoEmbeddedLink
    );
  }

  get shouldShowPdf(): boolean {
    return this.offer.offerType?.acceptedMimeType === 'application/pdf';
  }

  get isYouTubeVideo(): boolean {
    return (
      this.offer.videoEmbeddedLink?.includes('youtube.com') ||
      this.offer.videoEmbeddedLink?.includes('youtu.be') ||
      false
    );
  }

  get isVimeoVideo(): boolean {
    return this.offer.videoEmbeddedLink?.includes('vimeo.com') || false;
  }

  get safeVideoUrl(): SafeResourceUrl {
    if (this._cachedVideoUrl) {
      return this._cachedVideoUrl;
    }
    return this.generateSafeVideoUrl();
  }

  private generateSafeVideoUrl(): SafeResourceUrl {
    if (!this.offer.videoEmbeddedLink) {
      return this.sanitizer.bypassSecurityTrustResourceUrl('');
    }

    let embedUrl = this.offer.videoEmbeddedLink;

    if (this.isYouTubeVideo) {
      // YouTube
      if (embedUrl.includes('youtube.com/watch?v=')) {
        const videoId = embedUrl.split('v=')[1].split('&')[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      } else if (embedUrl.includes('youtu.be/')) {
        const videoId = embedUrl.split('youtu.be/')[1].split('?')[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      }
    } else if (this.isVimeoVideo) {
      // Vimeo
      const videoId = embedUrl.split('vimeo.com/')[1].split('?')[0];
      embedUrl = `https://player.vimeo.com/video/${videoId}?badge=0&autopause=0&player_id=0`;
    }

    return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
  }

  downloadPdf() {
    if (this.shouldShowPdf && this.showDownloadPdf) {
      window.open(this.offer.imageUrl, '_blank');
    }
  }
}
